import breakpointHelper from 'breakpoint-helper'
import prefetchImages from 'prefetch-image'

const instance = breakpointHelper({
  xs: '416px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
})

export const { getBreakpoints, getMediaQuery, isMatching, listen, listenAll } =
  instance

const animationImages = [
  '/images-optimized/Icon_Sicherheit_ani.gif',
  '/images-optimized/Icon_Couch_ani.gif',
  '/images-optimized/Icon_Jederzeit_ani.gif',
  '/images-optimized/Icon_Tranzparenz_ani.gif',
]

prefetchImages(animationImages, {}).catch(() => {
  /* best-effort preload */
})

const hasGsap = () =>
  typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined'

const hasDollar = () => typeof window !== 'undefined' && !!window.$

const setupChoiceTiles = () => {
  const selector = "*[data-ani='best-choice-tile']"
  let tween
  if (hasGsap()) {
    tween = gsap.fromTo(
      selector,
      { y: 150, autoAlpha: 0, opacity: 0 },
      {
        autoAlpha: 1,
        opacity: 1,
        duration: 1,
        y: 0,
        stagger: 0.25,
        scrollTrigger: {
          trigger: '.trigger-best-choice-ani',
          start: '100 80%',
          end: 'bottom 100px',
          toggleActions: 'restart none none reverse',
        },
      }
    )
  }

  let teardownEvents = () => {}
  if (hasDollar()) {
    const enterHandler = function () {
      const imgs = $(this).find('img')
      $(imgs[0]).addClass('hidden')
      $(imgs[1]).removeClass('hidden')
    }
    const leaveHandler = function () {
      const imgs = $(this).find('img')
      $(imgs[0]).removeClass('hidden')
      $(imgs[1]).addClass('hidden')
    }

    $(selector).on('mouseenter', function () {
      enterHandler.call(this)
    })
    $(selector).on('mouseleave', function () {
      leaveHandler.call(this)
    })

    teardownEvents = () => {
      $(selector).off('mouseenter', enterHandler)
      $(selector).off('mouseleave', leaveHandler)
    }
  }

  return () => {
    teardownEvents()
    tween?.kill?.()
  }
}

const setupBenefitsTiles = () => {
  const selector = "*[data-ani='benefits-tile']"
  let tween
  if (hasGsap()) {
    tween = gsap.fromTo(
      selector,
      { x: -300, autoAlpha: 0, opacity: 0, scale: 0.2 },
      {
        autoAlpha: 1,
        opacity: 1,
        scale: 1,
        duration: 0.7,
        ease: 'power4',
        x: 0,
        stagger: 0.3,
        scrollTrigger: {
          trigger: '#benefits-tiles',
          start: '100 80%',
          end: 'bottom 100px',
          toggleActions: 'restart none none reverse',
        },
      }
    )
  }

  return () => {
    tween?.kill?.()
  }
}

const scaleTopIframe = (value) => {
  const el = document.getElementById('topIframe')
  if (!el) return

  el.style.transformOrigin = '0% 50%'
  el.style.transform = `scaleX(${value}) scaleY(${value})`
}

const setRectIframe = (width, height) => {
  const el = document.getElementById('topIframe')
  if (!el) return

  el.height = height
  el.width = width
}

export const calculateIframeScale = () => {
  const container = document.getElementById('topFrameContainer')
  if (!container) return

  const rect = container.getBoundingClientRect()
  // Target a base size that keeps the canvas within the hero while filling width.
  const targetWidth = rect.width * 0.95
  const targetHeight = rect.height * 0.9
  const baseSize = 900 // original animation reference

  const factor = Math.min(targetWidth / baseSize, targetHeight / baseSize, 1)
  scaleTopIframe(factor)
  setRectIframe(targetWidth, targetHeight)
}

const setupResizeListener = () => {
  let resizeTimer
  const handleResize = () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(calculateIframeScale, 100)
  }

  window.addEventListener('resize', handleResize)
  return () => window.removeEventListener('resize', handleResize)
}

const setupBestChoiceGlider = () => {
  const gliderContainer = document.querySelector('[data-toggle="best-choice-glider"]')
  if (!gliderContainer || typeof Glider === 'undefined') return () => {}

  gliderContainer.classList.add('glider')
  const glider = new Glider(gliderContainer, {
    slidesToShow: 1,
    slidesToScroll: 1,
    draggable: true,
    rewind: true,
    duration: 0.35,
  })

  return () => glider.destroy()
}

const setupTestimonialsGlider = () => {
  const gliderContainer = document.querySelector('[data-toggle="testimonials-glider"]')
  if (!gliderContainer || typeof Glider === 'undefined') return () => {}

  gliderContainer.classList.remove('grid', 'grid-cols-2', 'md:grid-cols-4', 'gap-52')
  gliderContainer.classList.add('glider')
  const glider = new Glider(gliderContainer, {
    slidesToShow: 1,
    slidesToScroll: 1,
    draggable: true,
    rewind: true,
    duration: 0.3,
    responsive: [
      { breakpoint: 640, settings: { slidesToShow: 1.25, slidesToScroll: 1 } },
      { breakpoint: 768, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 1024, settings: { slidesToShow: 4, slidesToScroll: 1 } },
    ],
  })

  return () => glider.destroy()
}

const setupFaqToggles = () => {
  const buttons = Array.from(document.querySelectorAll('[data-toggle="collapsible"]'))
  const cleanups = []

  buttons.forEach((btn) => {
    const content = btn.nextElementSibling
    if (!content) return

    const icon = btn.querySelector('.text-blue-500')
    const toggle = () => {
      const willOpen = !content.classList.contains('open')
      content.classList.toggle('open', willOpen)
      if (icon) icon.textContent = willOpen ? '–' : '+'
    }

    btn.addEventListener('click', toggle)
    // ensure closed state by default
    content.classList.remove('open')
    cleanups.push(() => btn.removeEventListener('click', toggle))
  })

  return () => cleanups.forEach((fn) => fn())
}

export const setupLandingPage = () => {
  if (typeof window === 'undefined') return () => {}

  if (hasDollar()) {
    window.jQuery = window.$
  }

  if (typeof AOS !== 'undefined') {
    AOS.init()
  }

  if (typeof LazyLoad !== 'undefined') {
    const lazyLoadInstance = new LazyLoad({})
    lazyLoadInstance.update()
  }

  if (hasGsap()) {
    gsap.registerPlugin(ScrollTrigger)
  }

  calculateIframeScale()
  const teardownChoiceTiles = setupChoiceTiles()
  const teardownBenefits = setupBenefitsTiles()
  const teardownBestChoiceGlider = setupBestChoiceGlider()
  const teardownTestimonialsGlider = setupTestimonialsGlider()
  const teardownFaq = setupFaqToggles()
  const teardownResize = setupResizeListener()

  return () => {
    teardownChoiceTiles?.()
    teardownBenefits?.()
    teardownBestChoiceGlider?.()
    teardownTestimonialsGlider?.()
    teardownFaq?.()
    teardownResize()
  }
}
